/**
 * How a turn's evidence memory is assembled from the two SSE events.
 *
 * Extracted so the tests drive THIS function rather than a copy of it. The
 * first version of the test re-implemented the reducer inline; mutating the
 * component then left it green, which is the same mirror-versus-production
 * mistake that let an earlier bug through today. A mirror tests the mirror.
 */

export type TurnMemory = {
  concepts: Array<{ slug: string; display_name?: string; origin?: string | null }>;
  /**
   * Stances citable this turn whose parent concept did not rank.
   *
   * Rendered AFTER concepts, because Hermes numbers `[Cn]` across
   * concepts-then-direct. A drawer built from `concepts` alone leaves a direct
   * citation resolving to nothing, or to whichever concept shares the number.
   */
  direct_stances: Array<{
    id: string;
    slug: string | null;
    position_md?: string;
    authored_at?: string | null;
    origin?: string | null;
  }>;
  skills: unknown[];
  vault_passages: unknown[];
  external_sources: unknown[];
};

/**
 * Fold `evidence_final` onto what `memory_used` already delivered.
 *
 * `evidence_final` REPLACES the turn's memory, so a payload that omits
 * `direct_stances` would silently discard exactly the rows a direct citation
 * points at — and only on turns that emit a final event, which is how it
 * survives casual testing. When the field is present it wins, including when
 * it is empty: a stance dropped during compilation must stop being shown as
 * cited.
 */
export function applyEvidenceFinal(
  previous: TurnMemory | null,
  evidence: Partial<TurnMemory>,
): TurnMemory {
  return {
    concepts: evidence.concepts ?? [],
    direct_stances: evidence.direct_stances ?? previous?.direct_stances ?? [],
    skills: evidence.skills ?? [],
    vault_passages: evidence.vault_passages ?? [],
    external_sources: evidence.external_sources ?? [],
  };
}

/**
 * The economist's own views for this turn, in citation order.
 *
 * `[Cn]` is assigned by Hermes across concepts THEN direct stances, continuing
 * one sequence (`start=len(concepts) + 1`). Any surface that renders these rows
 * must use the same order or a citation points at the wrong row — and a
 * citation that resolves to the wrong evidence is worse than one that resolves
 * to nothing, because it looks correct.
 *
 * Returned as one list, numbered here, so the drawer cannot drift from the
 * prompt. Every consumer (public strip, authenticated debug panels) calls this
 * rather than re-deriving the offset.
 */
export function ownViewRows(memory: {
  concepts: TurnMemory['concepts'];
  direct_stances?: TurnMemory['direct_stances'];
}): Array<{ cite: string; key: string; label: string; stanceId?: string }> {
  const rows = memory.concepts.map((c) => ({
    key: `concept-${c.slug}`,
    label: c.display_name || c.slug,
  }));
  for (const s of memory.direct_stances ?? []) {
    rows.push({ key: `direct-${s.id}`, label: s.slug ?? '未关联概念观点' });
  }
  return rows.map((row, i) => ({
    ...row,
    cite: `C${i + 1}`,
    stanceId: (memory.direct_stances ?? [])[i - memory.concepts.length]?.id,
  }));
}

/** Whether this turn showed the visitor any evidence at all. */
export function hasAnyEvidence(memory: TurnMemory | null): boolean {
  if (!memory) return false;
  return (
    memory.concepts.length +
      memory.direct_stances.length +
      memory.skills.length +
      memory.vault_passages.length +
      memory.external_sources.length >
    0
  );
}
